import React, { useRef } from 'react';
import classNames from 'classnames';
import Equalizer from 'react-equalizer';
import { useMediaQuery } from 'react-responsive';
import ProLink from '../../../shared/ProLink/ProLink';
import { dataLayer, miniGazelle } from '../../../../helpers';

export default props => {
    const {
        title,
        showStickyItemsClasses,
        darkBlue,
        links,
        buttonMarkup,
        navClasses,
        listMarkup,
        wrapperId,
        navId,
        toggleExpandedNav,
        retired,
        showExpandedNav,
        logicalName
    } = props;

    const menuRef = useRef(null);
    const toggleButtonRef = useRef(null);

    const handleFocusOut = e => {
        // Check if the next focused element is outside of our menu
        if (!menuRef.current?.contains(e.relatedTarget) && showExpandedNav) {
            toggleExpandedNav(e);
        }
    };

    const wrappedListMarkup = React.Children.map(listMarkup, child => {
        if (!React.isValidElement(child)) return child;

        return React.cloneElement(child, {
            onClick: e => {
                toggleExpandedNav(e);
                toggleButtonRef.current?.focus();
            },
        });
    });

    const getColumns = () =>{
        const isMobile = useMediaQuery({ query: "(max-width: 481px)" });
        const isTablet = useMediaQuery({ query: "(max-width: 767px)" });
        const isSmallDesktop = useMediaQuery({ query: "(max-width: 1023px)" });

        if (isMobile) return 1;
        if (isTablet) return 2;
        if (isSmallDesktop) return 3;
        return 4;
    }

    const countNonNull = (obj) =>
        Object.entries(obj ?? {}).reduce((count, [key, val]) => {
            if (val === null) return count;
            if (retired && key === "whereToBuyLink") return count;
            if (typeof val === "object") return count + countNonNull(val);
            return count + 1;
    }, 0);

    const columns = getColumns();
    const buttonsPerRow = Math.ceil(countNonNull(links) / columns);

    const customEventHandler = ({label, destination}) => {
        dataLayer.triggerEvent("proComponentInteraction", {
            type: "nav-link",
            logicalName,
            label: label,
            destination : destination,
        });
        return true;
    }

    return (
        <div
            ref={menuRef}
            onBlurCapture={handleFocusOut}
            className="sticky-nav-inner"
            data-smart-affix="true"
            id={wrapperId}
        >
            {title &&<p className='h3 product-title'>{title}</p>}

            <div className={showStickyItemsClasses}>
                <div className="sticky-nav-flex-container">
                    <div className="header-row">
                        <button
                            ref={toggleButtonRef}
                            onClick={toggleExpandedNav}
                            data-testid="expand-nav"
                            aria-label="Toggle menu"
                            aria-expanded={showExpandedNav ? 'true' : 'false'}
                            aria-controls={`nav-list-${wrapperId}`}
                        >
                            <span className="bar" aria-hidden="true" />
                            <span className="bar" aria-hidden="true" />
                            <span className="bar" aria-hidden="true" />
                        </button>

                        <div className="h3 m-0 text-nowrap" data-testid="product-title" id={navId}>
                            {title}
                        </div>
                    </div>
                    
                    <div className="cta-buttons" style={links ? { gridTemplateRows: `repeat(${buttonsPerRow}, 1fr)` } : undefined}>
                        {links && links.exportLink && (
                            <ProLink
                                target="_blank"
                                rel="noopener noreferrer"
                                to={links.exportLink}
                                className="btn btn-lg btn-default export-pdf nowrap-btn"
                                onClick={() => customEventHandler({
                                    label: miniGazelle.label('export', true),
                                    destination: links.exportLink
                                }
                                )}
                            >
                                {miniGazelle.label('export', true)}
                            </ProLink>
                        )}
                        {!retired && links && links.whereToBuyLink && (
                            <ProLink
                                to={links.whereToBuyLink}
                                target="_blank"
                                rel="noopener" 
                                className="btn btn-lg btn-default btn-yellow where-to-buy nowrap-btn"
                                onClick={() => customEventHandler({
                                    label: miniGazelle.label('whereToBuy', true),
                                    destination: links.whereToBuyLink
                                }
                                )}
                            >
                                {miniGazelle.label('whereToBuy', true)}
                            </ProLink>
                        )}
                        {links && links.rfiLink && (
                            <ProLink
                                to={links.rfiLink}
                                target="_blank"
                                rel="noopener"
                                className="btn btn-lg btn-default nowrap-btn"
                                onClick={() => customEventHandler({
                                    label: miniGazelle.label('rfi', true),
                                    destination: links.rfiLink
                                }
                                )}
                            >
                                {miniGazelle.label('rfi', true)}
                            </ProLink>
                        )}
                        {links && links.additionalLinks && Object.entries(links.additionalLinks).map(([key, link]) => 
                            link && (
                                <ProLink
                                    key={key}
                                    to={link}
                                    target="_blank"
                                    rel="noopener"
                                    className={`btn btn-lg btn-default nowrap-btn`}
                                    onClick={() => customEventHandler({
                                        label: miniGazelle.label(key, false) || key,
                                        destination: link}
                                    )}
                                >
                                    {miniGazelle.label(key, false) || key}
                                </ProLink>
                            )
                        )}
                        {buttonMarkup}
                    </div>
                </div>
            </div>

            <div
                className={`container tw-px-0 ${showExpandedNav ? 'show-list' : 'hide-list'}`}
                aria-hidden={showExpandedNav ? 'false' : 'true'}
            >
                <div className="col-md-12">
                    <Equalizer byRow={false}>
                        <ul className={navClasses} id={`nav-list-${wrapperId}`}>
                            {wrappedListMarkup}
                            {links && links.exportLink && (
                                <ProLink
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    to={links.exportLink}
                                    className="btn btn-lg btn-default export-pdf-mobile text-nowrap text-nowrap"
                                >
                                    {miniGazelle.label('export', true)}
                                </ProLink>
                            )}
                        </ul>
                    </Equalizer>
                </div>
            </div>
        </div>
    );
};
